import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import type { GeoPoint } from '@app/shared/geo';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { RowMenu } from '../ui/RowMenu';
import { EVENTS_KEY } from '../events/labels';
import { leaveGuarded, sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

export const VENUES_RESOURCE = 'events.venues';
export const VENUES_KEY = ['venues'] as const;

/** A place of "Miejsca wydarzeń" as the server sends it (fields hidden from the role are absent). */
export type Venue = { id: string; name?: string; address?: string; point?: GeoPoint | null; googlePlaceId?: string; notes?: string; version: number };

function Problem({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="alert" role="alert">
      {error.status === 409 ? 'Ktoś już zmienił to miejsce. Zamknij i otwórz listę jeszcze raz.' : fieldIssues(error).map((i) => i.message).join(' ') || error.message}
    </p>
  );
}

/** Add or change a place: name, address, notes for the crew (the point comes from the event or Google). */
function VenueForm({ base, onDone }: { base: Venue | null; onDone: () => void }) {
  const queryClient = useQueryClient();
  const notesId = useId();
  const [name, setName] = useState(base?.name ?? '');
  const [address, setAddress] = useState(base?.address ?? '');
  const [notes, setNotes] = useState(base?.notes ?? '');
  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), address: address.trim(), notes: notes.trim() };
      return base ? api(`/api/events/venues/${base.id}`, { method: 'PATCH', body: { version: base.version, ...body } }) : api('/api/events/venues', { method: 'POST', body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: VENUES_KEY });
      await queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
      onDone();
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (name.trim() !== '') save.mutate();
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const opening = useOpening({ name, address, notes });
  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ name, address, notes }, opening),
    save: async () => {
      if (name.trim() === '') return false;
      await save.mutateAsync();
      return true;
    },
  });
  return (
    <form ref={guardRef} className="settings-form" aria-label={base ? `Edycja miejsca: ${base.name ?? ''}` : 'Nowe miejsce'} onSubmit={submit}>
      <label className="field">
        <span>Nazwa</span>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" placeholder="np. Teatr Stary w Lublinie" />
      </label>
      <label className="field">
        <span>Adres</span>
        <input value={address} onChange={(e) => setAddress(e.target.value)} autoComplete="off" placeholder="np. Jezuicka 18, 20-113 Lublin" />
      </label>
      <div className="field">
        <label htmlFor={notesId}>Notatki dla ekipy</label>
        <textarea id={notesId} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="np. wjazd od ul. Królewskiej, rampa 1,2 m, prąd 63 A" />
      </div>
      <Problem error={save.error} />
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending || name.trim() === ''}>
          {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj miejsce'}
        </button>
        <button className="btn" type="button" onClick={onDone}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

/**
 * "Miejsca wydarzeń" (spec 2026-10-22-mapy-google §3.3) in "Ustawienia firmy": the places the company plays at with
 * notes for the crew. Google places saved with events land here by themselves; others are added by hand.
 */
export function VenuesSettings({ me, heading = true }: { me: Me; heading?: boolean }) {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const list = useQuery({
    queryKey: [...VENUES_KEY, q.trim()],
    placeholderData: keepPreviousData,
    queryFn: () => api<{ venues: Venue[] }>(`/api/events/venues${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`),
  });
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [confirm, setConfirm] = useState<Venue | null>(null);
  const remove = useMutation({
    mutationFn: (v: Venue) => api(`/api/events/venues/${v.id}?version=${v.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setConfirm(null);
      await queryClient.invalidateQueries({ queryKey: VENUES_KEY });
    },
  });
  const canCreate = can(me, VENUES_RESOURCE, 'create');
  const canEdit = can(me, VENUES_RESOURCE, 'edit');
  const canDelete = can(me, VENUES_RESOURCE, 'delete');
  const venues = list.data?.venues ?? [];
  return (
    <section className="settings-section" aria-label="Miejsca wydarzeń" data-feature-id="events.venues">
      {heading && <h2>Miejsca wydarzeń</h2>}
      <p className="hint">Miejsca, w których gracie, z notatkami dla ekipy. Podpowiadają się pierwsze przy wpisywaniu miejsca wydarzenia; miejsca wybrane z podpowiedzi Google dopisują się same.</p>
      <input className="settings-search" type="search" aria-label="Szukaj miejsca" placeholder="Szukaj po nazwie albo adresie" value={q} onChange={(e) => setQ(e.target.value)} />
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać miejsc'}</p>}
      <ul className="settings-list" aria-label="Lista miejsc">
        {venues.map((v) =>
          editing === v.id ? (
            <li key={v.id}>
              <VenueForm base={v} onDone={() => setEditing(null)} />
            </li>
          ) : (
            <li key={v.id} className="settings-row">
              <div className="settings-row-main">
                <strong>{v.name}</strong>
                {v.address && <span className="muted">{v.address}</span>}
                {v.notes && <span className="venue-notes">{v.notes}</span>}
              </div>
              {confirm?.id === v.id ? (
                <span className="settings-confirm" role="group" aria-label={`Usunąć miejsce ${v.name ?? ''}?`}>
                  <span>Usunąć?</span>
                  <button className="btn small danger" type="button" disabled={remove.isPending} onClick={() => remove.mutate(v)}>
                    Usuń
                  </button>
                  <button className="btn small ghost" type="button" onClick={() => setConfirm(null)}>
                    Nie
                  </button>
                </span>
              ) : (
                (canEdit || canDelete) && (
                  <RowMenu
                    label={`Działania: ${v.name ?? ''}`}
                    actions={[...(canEdit ? [{ label: 'Edytuj', onSelect: () => leaveGuarded(() => setEditing(v.id)) }] : []), ...(canDelete ? [{ label: 'Usuń', danger: true, onSelect: () => setConfirm(v) }] : [])]}
                  />
                )
              )}
            </li>
          ),
        )}
      </ul>
      {list.data && venues.length === 0 && <p className="muted">{q.trim() ? 'Nie znaleziono takiego miejsca.' : 'Lista jest pusta — zapełni się, gdy będziecie wybierać miejsca wydarzeń z podpowiedzi.'}</p>}
      <Problem error={remove.error} />
      {canCreate && editing === 'new' && <VenueForm base={null} onDone={() => setEditing(null)} />}
      {canCreate && editing !== 'new' && (
        <button className="btn small" type="button" onClick={() => leaveGuarded(() => setEditing('new'))}>
          + Dodaj miejsce
        </button>
      )}
      <p className="hint">Usunięte miejsce znika z podpowiedzi; wydarzenia zachowują swój adres, ale notatki przestają się przy nich pokazywać.</p>
    </section>
  );
}

/** The notes of one place, from the event card (⚙ "Notatki o miejscu…"). */
export function VenueNotesForm({ venueId, onDone }: { venueId: string; onDone: () => void }) {
  const venue = useQuery({ queryKey: [...VENUES_KEY, 'one', venueId], queryFn: () => api<Venue>(`/api/events/venues/${venueId}`) });
  if (venue.error) return <p className="alert" role="alert">{venue.error instanceof ApiError ? venue.error.message : 'Nie udało się wczytać miejsca'}</p>;
  if (!venue.data) return <p className="muted">Wczytywanie…</p>;
  return <VenueForm base={venue.data} onDone={onDone} />;
}
